import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";

import { useAuthContext } from "../../../context/AuthContext";
import { useNotificationsOptional } from "../../../context/NotificationContext";
import { useConversations } from "../../../hooks/useConversations";
import { useConversationDetails } from "../../../hooks/useConversationDetails";
import { useMessages } from "../../../hooks/useMessages";
import { useRealtimeMessages } from "../../../hooks/useRealtimeMessages";
import { getConversationMessagesById, deleteConversationMessage } from "../../../api/client";
import { useCreateConversation } from "../../../hooks/useCreateConversation"; 
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import { getConversationInsights } from "../../../api/client";

// ✅ Função auxiliar para padronizar o status
const getStatusLabelFromCode = (status) => {
  const statusMap = {
    0: "Pendente",
    1: "Em Atendimento",
    2: "Concluída",
    3: "Contato Futuro",
    4: "Perdida",
    5: "Cancelada",
  };

  if (typeof status === "string" && !isNaN(Number(status))) {
    status = Number(status);
  }

  return statusMap[status] ?? "Desconhecido";
};

const CHAT_FILTERS_STORAGE_KEY = "xpro_chat_filters";
const CHAT_SEARCH_STORAGE_KEY = "xpro_chat_search";
const CHAT_LAST_SEEN_CLIENT_AT_KEY = "xpro_chat_last_seen_client_at";

/** Foco do atendente ao logar: minhas solicitações pendentes e em atendimento ("Meu dia") */
const DEFAULT_FILTERS = [
  "Minhas Solicitações",
  "Pendente",
  "Em Atendimento",
];

function loadPersistedFilters() {
  try {
    const raw = localStorage.getItem(CHAT_FILTERS_STORAGE_KEY);
    if (raw == null) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_FILTERS;
    const valid = parsed.filter((f) => typeof f === "string");
    return valid.length ? valid : DEFAULT_FILTERS;
  } catch {
    return DEFAULT_FILTERS;
  }
}

function loadPersistedSearch() {
  try {
    const s = localStorage.getItem(CHAT_SEARCH_STORAGE_KEY);
    return typeof s === "string" ? s : "";
  } catch {
    return "";
  }
}

function loadLastSeenClientAt() {
  try {
    const raw = localStorage.getItem(CHAT_LAST_SEEN_CLIENT_AT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export default function useChatState() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const { userData } = useAuthContext();
  const notificationsCtx = useNotificationsOptional();

  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messagesMap, setMessagesMap] = useState(new Map());
  const messages = Array.from(messagesMap.values());

  const onSocketActivity = useCallback(
    (evt) => {
      const isConversationSelected =
        selectedConversationId != null &&
        String(selectedConversationId) === String(evt.conversationId);
      notificationsCtx?.dispatchRealtimeSocket?.({
        ...evt,
        isConversationSelected,
      });
    },
    [notificationsCtx, selectedConversationId]
  );
  const { sendMessageMutation } = useMessages();

  const { createConversation, isCreating, errorCreating } = useCreateConversation();

  const [aiInsights, setAiInsights] = useState(null);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [aiInsightsError, setAiInsightsError] = useState(null);
  const [aiInsightsConversationId, setAiInsightsConversationId] = useState(null);

  const [initialLoading, setInitialLoading] = useState(true);
  const [showConversationList, setShowConversationList] = useState(true);
  const [showMessageHistory, setShowMessageHistory] = useState(!isMobile);
  const [showConversationDetails, setShowConversationDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState(loadPersistedSearch);
  const [filters, setFilters] = useState(loadPersistedFilters);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [requestTotal, setRequestTotal] = useState(false);
  const hasRequestedTotal = useRef(false);
  const [lastSeenClientAtMap, setLastSeenClientAtMap] = useState(loadLastSeenClientAt);
  const [messagesPageState, setMessagesPageState] = useState({
    hasMore: false,
    lastMessageId: null,
    isInitialLoaded: false,
    isLoadingMore: false,
  });

  // Persistir filtros e busca para manter após reload
  useEffect(() => {
    try {
      localStorage.setItem(CHAT_FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (e) {
      console.warn("Falha ao salvar filtros do chat no localStorage", e);
    }
  }, [filters]);

  useEffect(() => {
    try {
      localStorage.setItem(CHAT_SEARCH_STORAGE_KEY, searchTerm);
    } catch (e) {
      console.warn("Falha ao salvar busca do chat no localStorage", e);
    }
  }, [searchTerm]);

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);
  const debouncedFilters = useDebouncedValue(filters, 400);

  const {
    data: conversationDetails,
    isLoading: isLoadingDetails,
    refetch: refetchConversationDetails,
  } = useConversationDetails(selectedConversationId);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    refetch,
  } = useConversations(
    debouncedSearchTerm,
    debouncedFilters,
    (() => {
      const quemFilters = [
        "Minhas Solicitações",
        "Outros Usuários",
        "Fila",
        "ChatBots",
        "Agentes de IA",
      ];
      const selectedQuem = (debouncedFilters || []).filter((f) =>
        quemFilters.includes(f)
      );
      const multipleQuem = selectedQuem.length > 1;

      const tipoFilters = ["ChatBots", "Agentes de IA", "Fila"];
      const selectedTipo = (debouncedFilters || []).filter((f) => tipoFilters.includes(f));
      const singleTipo =
        selectedTipo.length === 1 ? selectedTipo[0] : null;
      const tipoMap = {
        ChatBots: "chatbots",
        "Agentes de IA": "agentes",
        Fila: "fila",
      };
      // Com 2+ opções em Quem, a lista usa OR no cliente; tipo_responsavel na API seria AND e esconderia resultados.
      const tipoResponsavel =
        !multipleQuem && singleTipo && tipoMap[singleTipo]
          ? tipoMap[singleTipo]
          : null;

      return {
        tipo_scope: "relacionamento",
        default_date_window_days: 30,
        ...(tipoResponsavel ? { tipo_responsavel: tipoResponsavel } : {}),
      };
    })(),
    requestTotal
  );

  // Pedir total no banco só após a 1ª carga (evita COUNT pesado no primeiro request)
  useEffect(() => {
    if (data?.pages?.length > 0 && !hasRequestedTotal.current) {
      hasRequestedTotal.current = true;
      setRequestTotal(true);
    }
  }, [data?.pages?.length]);

  const conversationsFromApi = data?.pages?.flatMap((page) => page.list) ?? [];
  const conversations = conversationsFromApi.map((c) => {
    const lastClientAt = c.lastClientMessageAt;
    const seenAt = lastSeenClientAtMap?.[c.id] ?? null;
    const hasNewClientReply =
      lastClientAt &&
      (!seenAt || new Date(lastClientAt).getTime() > new Date(seenAt).getTime());

    return {
      ...c,
      unreadClient: hasNewClientReply ? 1 : 0,
    };
  });
  const totalInDatabase = data?.pages?.[0]?.totalInDatabase;

  // Throttle para evitar spam de refetch quando chegam muitos eventos socket.
  const lastListRefreshAtRef = useRef(0);
  const listRefreshCooldownMs = 2500;
  const listRefreshRefetch = useCallback(() => {
    const now = Date.now();
    if (now - lastListRefreshAtRef.current < listRefreshCooldownMs) return;
    lastListRefreshAtRef.current = now;
    if (typeof refetch === "function") refetch();
  }, [refetch]);

  // Reconcile do histórico usando o endpoint paginado correto.
  // Mantém mensagens já carregadas (merge idempotente) para não quebrar paginação/scroll.
  const messagesSyncInFlightRef = useRef(false);
  const syncLatestConversationMessagesById = useCallback(
    async (conversationId, { limit = 50 } = {}) => {
    if (!conversationId) return;
    if (messagesSyncInFlightRef.current) return;
    messagesSyncInFlightRef.current = true;
    try {
      const payload = await getConversationMessagesById(String(conversationId), {
        limit,
        returnRaw: true,
      });

      const list = Array.isArray(payload?.messages)
        ? payload.messages
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

      setMessagesMap((prev) => {
        const next = new Map(prev);

        const mergeKeepingLocalMedia = (prevMsg, incoming) => {
          if (!prevMsg) return incoming;
          const merged = { ...prevMsg, ...incoming };
          const keepIfMissing = ["image", "audio", "video", "file", "document", "location"];
          keepIfMissing.forEach((k) => {
            if (
              (merged[k] === null || merged[k] === undefined || merged[k] === "") &&
              prevMsg[k]
            ) {
              merged[k] = prevMsg[k];
            }
          });
          return merged;
        };

        for (const msg of list) {
          if (!msg || msg.id == null) continue;
          const existing = next.get(msg.id);
          next.set(msg.id, mergeKeepingLocalMedia(existing, msg));
        }

        return next;
      });
    } catch (err) {
      console.error("Erro ao sincronizar mensagens (realtime/polling):", err);
    } finally {
      messagesSyncInFlightRef.current = false;
    }
    },
    []
  );

  const markClientMessagesAsSeen = (conversation) => {
    if (!conversation) return;
    const convId = conversation.id;
    const lastClientAt = conversation.lastClientMessageAt;
    if (!convId || !lastClientAt) return;

    setLastSeenClientAtMap((prev) => {
      const next = {
        ...prev,
        [convId]: lastClientAt,
      };
      try {
        localStorage.setItem(CHAT_LAST_SEEN_CLIENT_AT_KEY, JSON.stringify(next));
      } catch (e) {
        console.warn("Falha ao salvar lastSeenClientAt no localStorage", e);
      }
      return next;
    });
  };

  const loadAiInsights = async (conversationIdParam) => {
    const conversationIdToUse = conversationIdParam || selectedConversationId;
    if (!conversationIdToUse) return;

    // Se já temos insights para esta conversa específica, não recarrega
    if (
      aiInsights &&
      aiInsightsConversationId != null &&
      String(aiInsightsConversationId) === String(conversationIdToUse)
    ) {
      return;
    }

    const requestConversationId = String(conversationIdToUse);

    try {
      setIsLoadingInsights(true);
      setAiInsightsError(null);

      const data = await getConversationInsights(requestConversationId);

      // Se, quando a resposta chegou, já mudamos de conversa, ignora este resultado
      if (String(selectedConversationId) !== String(requestConversationId)) {
        return;
      }

      setAiInsights(data || null);
      setAiInsightsConversationId(requestConversationId);

      // também grava no objeto da conversa atual para reaproveitar ao reabrir o acordeão
      setSelectedConversation((prev) => {
        if (!prev || prev.id !== requestConversationId) return prev;
        return { ...prev, aiInsights: data };
      });
    } catch (err) {
      console.error("Erro ao carregar insights da conversa:", err);

      // só aplica o erro se ainda estivermos na mesma conversa
      if (String(selectedConversationId) === String(requestConversationId)) {
        setAiInsightsError(err?.message || "Erro ao carregar insights da conversa.");
      }
    } finally {
      if (String(selectedConversationId) === String(requestConversationId)) {
        setIsLoadingInsights(false);
      }
    }
  };

  useRealtimeMessages({
    selectedConversationId,
    refetchConversationDetails,
    refetchConversations: listRefreshRefetch,
    refreshConversationMessages: syncLatestConversationMessagesById,
    onSocketActivity,
  });

  useEffect(() => {
    if (!conversationDetails?.data) return;

    const d = conversationDetails.data; // payload do endpoint /conversations/:id
    // Backend pode enviar agente no objeto principal ou aninhado (ex.: request, solicitation)
    const req = d.request ?? d.solicitation ?? d.demand ?? d;

    // Se a conversa foi aberta diretamente pela URL em /chat/:id
    // e o tipo dela é "comercial" (typeType === 0), redireciona
    // automaticamente para o pipeline de vendas.
    const isChatRoute =
      location.pathname === "/chat" || location.pathname.startsWith("/chat/");
    const rawTypeType = d.typeType ?? req.typeType ?? null;
    const numericTypeType =
      rawTypeType !== null && rawTypeType !== undefined
        ? Number(rawTypeType)
        : null;

    if (
      isChatRoute &&
      id &&
      String(d.id) === String(id) &&
      numericTypeType === 0
    ) {
      navigate(`/sales-pipeline/${d.id}`, { replace: true });
      return;
    }

    const agentId =
      d.agentId ?? d.id_agente ?? req.agentId ?? req.id_agente ?? null;
    const agentName =
      d.agentName ?? d.agent_name ?? req.agentName ?? req.agent_name ?? null;
    const client = d.client ?? null;
    // normalização única para a UI:
    const normalized = {
      id: d.id,
      status: d.status,
      statusLabel: getStatusLabelFromCode(d.status),
      projectId: d.projectId ?? req.projectId ?? null,
      channelId: d.channelId ?? req.channelId ?? null,
      typeId: d.typeId ?? req.typeId ?? null,
      typeType: rawTypeType,
      subtype1: d.subtype1 ?? req.subtype1 ?? null,
      subtype2: d.subtype2 ?? req.subtype2 ?? null,
      subtype3: d.subtype3 ?? req.subtype3 ?? null,
      userId: d.userId ?? req.userId ?? null,
      userName: d.userName ?? req.userName ?? null,
      agentId,
      agentName,
      potential: d.potential ?? req.potential ?? null,
      nextContactAt: d.nextContactAt ?? req.nextContactAt ?? null,
      nf: d.nf ?? req.nf ?? null,

      // mapeia “cliente” para os nomes esperados pelos componentes
      clientId: d.clientId ?? null,
      nome: client?.name ?? "",
      email: client?.email ?? "",
      celular: client?.phone ?? "",
      cep: client?.zip ?? "",
      endereco: client?.address ?? "",
      numero: client?.addressNumber ?? "",
      complemento: client?.addressComplement ?? "",
      bairro: client?.neighborhood ?? "",
      cidade: client?.city ?? client?.locality ?? null,
      uf: client?.state ?? null,
      clientPotential: client?.potential ?? null,
      // Se o cliente atual não vier com grupo/tags, NÃO herdar do anterior.
      id_grupo: client?.id_grupo ?? null,
      clientTags: client?.tags ?? "",
      tags: d.tags ?? null,

      // channel string (ex.: “Ecommerce”)
      channel: d.channel ?? null,
      channelType: d.channelType ?? d.channel_type ?? null,

      // messages para o histórico (carregadas separadamente via /conversations/:id/messages)
      messages: Array.isArray(d.messages) ? d.messages : [],
      products: Array.isArray(d.products) ? d.products : [],
      history: Array.isArray(d.history) ? d.history : [],
      aiInsights,
    };

    // Usa sempre `prev` para não "colar" dados de outra conversa.
    setSelectedConversation((prev) => {
      const prevId = prev?.id != null ? String(prev.id) : null;
      const nextId = normalized?.id != null ? String(normalized.id) : null;
      if (prevId && nextId && prevId !== nextId) {
        return { ...normalized };
      }
      return { ...(prev || {}), ...normalized };
    });
    setIsLoadingConversation(false);
  }, [conversationDetails]);

  useEffect(() => {
    const isChatRoute =
      location.pathname === "/chat" || location.pathname.startsWith("/chat/");
    if (!isChatRoute) return;

    if (id) {
      setSelectedConversationId(id);
    }
  }, [id, location.pathname]);

  // Ao trocar de conversa (ex.: clique em notificação), zera header/estado para não mostrar dados do cliente anterior.
  useEffect(() => {
    if (!selectedConversationId) return;
    setIsLoadingConversation(true);
    setSelectedConversation(null);
    setAiInsights(null);
    setAiInsightsError(null);
    setAiInsightsConversationId(null);
  }, [selectedConversationId]);

  // Carrega a primeira página de mensagens sempre que uma nova conversa é selecionada
  useEffect(() => {
    const loadInitialMessages = async () => {
      if (!selectedConversationId) return;
      setMessagesPageState((prev) => ({
        ...prev,
        isInitialLoaded: false,
        isLoadingMore: true,
      }));
      try {
        const payload = await getConversationMessagesById(selectedConversationId, {
          limit: 50,
          returnRaw: true,
        });
        const list = Array.isArray(payload.messages)
          ? payload.messages
          : Array.isArray(payload.data)
            ? payload.data
            : [];

        setMessagesMap((prev) => {
          const next = new Map();
          for (const msg of list) {
            next.set(msg.id, msg);
          }
          // preserva mensagens locais pendentes (ex.: envios em andamento)
          prev.forEach((msg, key) => {
            const isPending =
              String(key).startsWith("temp-") ||
              msg.status === "sending" ||
              msg.status === "error";
            if (isPending && !next.has(key)) next.set(key, msg);
          });
          return next;
        });

        const pagination = payload.pagination || {};
        setMessagesPageState({
          hasMore: Boolean(pagination.hasMore),
          lastMessageId: pagination.lastMessageId ?? null,
          isInitialLoaded: true,
          isLoadingMore: false,
        });
      } catch (err) {
        console.error("Erro ao carregar mensagens da conversa:", err);
        setMessagesPageState((prev) => ({
          ...prev,
          isLoadingMore: false,
          isInitialLoaded: true,
        }));
      }
    };

    // ao trocar de conversa, limpa mensagens anteriores antes de carregar
    setMessagesMap(new Map());
    if (selectedConversationId) {
      loadInitialMessages();
    }
  }, [selectedConversationId]);

  // Reconciliação por polling (rede de segurança quando o socket não chega).
  // Rodamos apenas quando a aba está visível.
  const selectedConversationIdRef = useRef(selectedConversationId);
  useEffect(() => {
    selectedConversationIdRef.current = selectedConversationId;
  }, [selectedConversationId]);

  const pagesLenRef = useRef(data?.pages?.length ?? 0);
  useEffect(() => {
    pagesLenRef.current = data?.pages?.length ?? 0;
  }, [data?.pages?.length]);

  useEffect(() => {
    const isChatRoute =
      location.pathname === "/chat" || location.pathname.startsWith("/chat/");
    if (!isChatRoute) return;

    if (typeof document === "undefined") return;

    const runHistorySync = () => {
      if (document.visibilityState !== "visible") return;
      const activeId = selectedConversationIdRef.current;
      if (activeId) syncLatestConversationMessagesById(activeId, { limit: 50 });
    };

    const runListSync = () => {
      if (document.visibilityState !== "visible") return;
      // Evita churn na paginação infinita: só refaz quando normalmente temos 1 página.
      if ((pagesLenRef.current ?? 0) <= 1) listRefreshRefetch();
    };

    const syncNow = () => {
      runHistorySync();
      runListSync();
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") syncNow();
    };

    window.addEventListener("focus", syncNow);
    document.addEventListener("visibilitychange", handleVisibility);

    const historyIntervalMs = 12000;
    const listIntervalMs = 24000;

    const historyTimer = setInterval(runHistorySync, historyIntervalMs);
    const listTimer = setInterval(runListSync, listIntervalMs);

    // Um sync inicial ao entrar/voltar para a rota de chat.
    syncNow();

    return () => {
      window.removeEventListener("focus", syncNow);
      document.removeEventListener("visibilitychange", handleVisibility);
      clearInterval(historyTimer);
      clearInterval(listTimer);
    };
  }, [
    location.pathname,
    listRefreshRefetch,
    syncLatestConversationMessagesById,
  ]);

  useEffect(() => {
    if (!isLoading) setInitialLoading(false);
  }, [isLoading]);

  const handleSelectConversation = (conversation) => {
    if (!conversation?.id) return;

    const nextId = String(conversation.id);
    const isSameConversation =
      selectedConversationId != null &&
      String(selectedConversationId) === nextId;

    // Sempre atualiza dados básicos e marca mensagens do cliente como lidas
    setSelectedConversation((prev) => {
      const base = { ...(prev || {}), ...conversation };
      const hasClientTags =
        conversation?.clientTags != null ||
        conversation?.client?.tags != null;
      const hasClientGroup =
        conversation?.id_grupo != null ||
        conversation?.client?.id_grupo != null;
      const hasConversationTags = conversation?.tags != null;

      return {
        ...base,
        // Defesa: não deixar tags/grupo “vazarem” ao trocar de conversa
        ...(hasClientTags ? {} : { clientTags: "" }),
        ...(hasClientGroup ? {} : { id_grupo: null }),
        ...(hasConversationTags ? {} : { tags: null }),
      };
    });
    markClientMessagesAsSeen(conversation);

    if (isMobile) {
      setShowMessageHistory(true);
      setShowConversationList(false);
      setShowConversationDetails(false);
    }

    // Se for a mesma conversa, apenas garante a navegação/visibilidade,
    // sem forçar estado de "carregando" infinito.
    if (isSameConversation) {
      setIsLoadingConversation(false);
      navigate(`/chat/${conversation.id}`);
      return;
    }

    // Nova conversa: dispara carregamento de detalhes e mensagens
    setIsLoadingConversation(true);
    setAiInsights(null);
    setAiInsightsError(null);
    setAiInsightsConversationId(null);
    setSelectedConversationId(nextId);
    navigate(`/chat/${conversation.id}`);
  };

  const loadOlderMessages = async () => {
    if (!selectedConversationId) return;
    if (messagesPageState.isLoadingMore || !messagesPageState.hasMore) return;

    const currentMessages = Array.from(messagesMap.values());
    if (!currentMessages.length) return;

    const sortedByIdAsc = [...currentMessages].sort((a, b) => {
      const idA = typeof a.id === "number" ? a.id : Number(a.id) || 0;
      const idB = typeof b.id === "number" ? b.id : Number(b.id) || 0;
      return idA - idB;
    });
    const oldest = sortedByIdAsc[0];
    if (!oldest?.id) return;

    setMessagesPageState((prev) => ({ ...prev, isLoadingMore: true }));

    try {
      const payload = await getConversationMessagesById(selectedConversationId, {
        limit: 50,
        beforeMessageId: oldest.id,
        returnRaw: true,
      });
      const list = Array.isArray(payload.messages)
        ? payload.messages
        : Array.isArray(payload.data)
          ? payload.data
          : [];

      setMessagesMap((prev) => {
        const next = new Map(prev);
        for (const msg of list) {
          if (!next.has(msg.id)) {
            next.set(msg.id, msg);
          }
        }
        return next;
      });

      const pagination = payload.pagination || {};
      setMessagesPageState((prev) => ({
        ...prev,
        hasMore: Boolean(pagination.hasMore),
        lastMessageId: pagination.lastMessageId ?? prev.lastMessageId,
        isLoadingMore: false,
      }));
    } catch (err) {
      console.error("Erro ao carregar mensagens antigas:", err);
      setMessagesPageState((prev) => ({ ...prev, isLoadingMore: false }));
    }
  };

  const handleEndConversation = () => {
    alert("Conversa encerrada");
    setSelectedConversation(null);
    setMessagesMap(new Map());
    if (isMobile) {
      setShowConversationList(true);
      setShowMessageHistory(false);
    }
    navigate("/chat");
  };

  const handleDeleteMessage = useCallback(
    async (messageId) => {
      if (!selectedConversationId || messageId == null) return;
      const mid = typeof messageId === "number" ? messageId : Number(messageId);
      if (!Number.isFinite(mid)) return;
      await deleteConversationMessage(selectedConversationId, mid);
      setMessagesMap((prev) => {
        const next = new Map(prev);
        next.delete(mid);
        return next;
      });
    },
    [selectedConversationId]
  );

  const containerStyle = {
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 1px 4px rgba(0, 0, 0, 0.5)"
        : "0 1px 4px rgba(0, 0, 0, 0.1)",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "8px",
    overflow: "hidden",
  };

  return {
    theme,
    isMobile,
    userData,
    selectedConversationId,
    selectedConversation,
    messages,
    showConversationList,
    showMessageHistory,
    showConversationDetails,
    isLoadingDetails,
    initialLoading,
    isLoadingConversation,
    searchTerm,
    filters,
    conversations,
    totalInDatabase,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    refetch,
    containerStyle,
    setSearchTerm,
    setFilters,
    setShowConversationList,
    setShowMessageHistory,
    setShowConversationDetails,
    setSelectedConversation,
    setMessages: setMessagesMap,
    handleSelectConversation,
    handleEndConversation,
    conversationDetails,
    refetchConversationDetails,
    sendMessageMutation,
    isError,
    createConversation,
    isCreatingConversation: isCreating,
    errorCreatingConversation: errorCreating,
    aiInsights,
    isLoadingInsights,
    aiInsightsError,
    loadAiInsights,
    hasMoreMessages: messagesPageState.hasMore,
    isLoadingMoreMessages: messagesPageState.isLoadingMore,
    loadOlderMessages,
    handleDeleteMessage,
  };
}
